namespace Solovis.Reports.ReportHelpers.AttributionReport {

  export interface ResolvedAttributionInterval {
    fromDayId: number | null;
    toDayId: number;
  }

  const YEARS_BACK_BY_PERIOD = {
    oneYear: 1,
    threeYears: 3,
    fiveYears: 5,
    sevenYears: 7,
    tenYears: 10
  };

  function getYearsBackStartDayId(yearsBack: number, asOfDayId: number): number {
    let dayId = asOfDayId;
    const monthsBack = yearsBack * 12;
    for (let i = 0; i < monthsBack; i++) {
      dayId = InvData.Util.DateIndexHelper.getDateIndexOfLastDayInPreviousMonth(dayId);
    }
    return dayId;
  }

  /**
   * Resolves the Attribution Report's "timePeriod" parameter (AttributionTimePeriodOptions, AR-010)
   * plus "asOfDate" into a concrete fromDayId/toDayId range for an IntervalStats request.
   *
   * Pure client-side date math using InvData.Util.DateIndexHelper -- the same primitives
   * MultiLevelAttribution.js already uses for MTD/QTD/YTD/years-back requests. "Since Inception"
   * resolves to fromDayId: null, matching InvData.Util.IntervalSetGenerator's own convention for an
   * open-ended lower bound (server-side resolves the actual earliest available data).
   *
   * "Since Dates" allows selecting more than one date in the Edit Time Periods dialog, but this
   * report only ever shows a single resolved interval -- the earliest selected date is used as the
   * lower bound. If AR-006-1 or a later story needs one column/interval per Since Date, that's a
   * larger change to the dataSource/table shape, not just this resolver.
   */
  export function resolveAttributionTimePeriod(
    timePeriod: Solovis.Reports.Parameters.AttributionReport.AttributionTimePeriodOptions,
    asOfDayId: number,
    fiscalYearStartMonth: number
  ): ResolvedAttributionInterval | null {
    if (!timePeriod || !timePeriod.selectedPeriod || !asOfDayId) {
      return null;
    }

    const period = timePeriod.selectedPeriod;
    const DateIndexHelper = InvData.Util.DateIndexHelper;

    switch (period) {
      case "mtd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousMonth(asOfDayId), toDayId: asOfDayId };
      case "qtd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousQuarter(asOfDayId), toDayId: asOfDayId };
      case "ytd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousYear(asOfDayId), toDayId: asOfDayId };
      case "fytd":
        return { fromDayId: DateIndexHelper.getDateIndexOfLastDayInPreviousFiscalYear(asOfDayId, fiscalYearStartMonth), toDayId: asOfDayId };
      case "sinceInception":
        return { fromDayId: null, toDayId: asOfDayId };
      case "sinceDates": {
        const sinceDayIds = timePeriod.sinceDayIds || [];
        if (!sinceDayIds.length) {
          return null;
        }
        return { fromDayId: Math.min(...sinceDayIds), toDayId: asOfDayId };
      }
      default: {
        const yearsBack = YEARS_BACK_BY_PERIOD[period];
        if (!yearsBack) {
          return null;
        }
        return { fromDayId: getYearsBackStartDayId(yearsBack, asOfDayId), toDayId: asOfDayId };
      }
    }
  }
}
